import { DatasetState } from "@/store/slice/datasetSlice";
import { Dataset, WeightValueEntry, WeightValues } from "@/types/dataset";

import { getAvailableDatasets } from "./dataset";
import { getValidNumericAttributes } from "./datasetAttributes";

export type WeightPrimitive =
  | WeightValueEntry
  | string
  | number
  | undefined
  | null;

export const normalizeWeightEntry = (
  entry: WeightValueEntry,
  defaultWeight = "0"
): WeightValueEntry => {
  if (!entry) return { weight: defaultWeight };

  if (typeof entry === "string") return { weight: entry };

  return {
    weight: entry.weight ?? defaultWeight,
    ...(entry.negative_impact !== undefined
      ? { negative_impact: entry.negative_impact }
      : {})
  };
};

export const getWeightString = (entry?: WeightPrimitive): string => {
  if (entry === null || entry === undefined) return "";
  if (typeof entry === "number") {
    return Number.isFinite(entry) ? entry.toString() : "";
  }
  if (typeof entry === "string") {
    return entry;
  }

  return entry.weight ?? "";
};

export const getNegativeImpactValue = (
  entry?: WeightPrimitive
): boolean | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  return entry.negative_impact;
};

export const getPercentageValue = (
  entry?: WeightPrimitive
): string | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  return entry.percentage;
};

export const parseWeightValue = (entry?: WeightPrimitive): number => {
  const value = getWeightString(entry);
  if (!value) return 0;
  const parsed = Number.parseFloat(value.replaceAll(",", ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const hasNonZeroValue = (entry?: WeightPrimitive | null): boolean => {
  return parseWeightValue(entry) !== 0;
};

export const calculateWeightPercentage = (
  datasetId: string,
  attributeIdx: string,
  totalWeight: number,
  localWeightValues: WeightValues
): string => {
  if (totalWeight === 0) return "0%";

  const datasetWeights = localWeightValues[datasetId];
  if (!datasetWeights) return "0%";

  const targetWeightEntry =
    attributeIdx === "weight"
      ? datasetWeights.weight
      : datasetWeights.attributes?.[attributeIdx];

  const weight = parseWeightValue(targetWeightEntry);

  const percentage = (weight / totalWeight) * 100;
  // Remove .00 if the percentage is a whole number
  const formattedPercentage = percentage.toFixed(2).replace(/\.00$/, "");
  return `${formattedPercentage}%`;
};

const NON_POI_SPATIAL_TYPES = ["polygon", "adm_area", "H3", "geohash", "line"];

/** Calculate default weight based on spatial type */
const getDefaultWeightForDataset = (dataset: Dataset): number => {
  if (NON_POI_SPATIAL_TYPES.includes(dataset.properties.spatialType)) {
    return getValidNumericAttributes(dataset.categories).length;
  }
  if (dataset.properties.spatialType === "poi") return 1;
  return dataset.categories.length;
};

/** Get valid attributes for a dataset based on its spatial type */
const getValidAttributesForDataset = (dataset: Dataset) => {
  if (NON_POI_SPATIAL_TYPES.includes(dataset.properties.spatialType)) {
    return getValidNumericAttributes(dataset.categories);
  }
  return dataset.categories[0] ? [dataset.categories[0]] : [];
};

/** Calculate total weight across all datasets */
const calculateTotalDatasetWeight = (state: DatasetState): number => {
  let total = 0;
  Object.entries(getAvailableDatasets(state.datasets)).forEach(
    ([datasetId, dataset]) => {
      let weight = parseWeightValue(state.weightValues[datasetId]?.weight);
      if (weight === 0 && !state.weightValues[datasetId]?.weight) {
        weight = getDefaultWeightForDataset(dataset);
      }
      total += weight;
    }
  );
  return total;
};

/** Calculate sum of attribute weights for a dataset */
const calculateAttributeWeightSum = (
  state: DatasetState,
  datasetId: string,
  validAttributes: { id: string }[]
): number =>
  validAttributes.reduce(
    (sum, attr) =>
      sum +
      parseWeightValue(state.weightValues[datasetId]?.attributes[attr.id]),
    0
  );

/** Update percentage for a single attribute */
const updateAttributePercentage = (
  state: DatasetState,
  datasetId: string,
  attributeId: string,
  attributeWeightSum: number,
  datasetPercentage: string
) => {
  const attrPercentageWithinDataset = calculateWeightPercentage(
    datasetId,
    attributeId,
    attributeWeightSum,
    state.weightValues
  );

  const datasetPercentageValue = Number.parseFloat(datasetPercentage);
  const finalAttrPercentage =
    attributeWeightSum === 0
      ? "0"
      : (
          (Number.parseFloat(attrPercentageWithinDataset) *
            datasetPercentageValue) /
          100
        )
          .toFixed(2)
          .replace(/\.00$/, "");

  const currentAttr = state.weightValues[datasetId].attributes[attributeId];
  if (typeof currentAttr === "object" && currentAttr !== null) {
    currentAttr.percentage = finalAttrPercentage;
  } else {
    state.weightValues[datasetId].attributes[attributeId] = {
      weight: String(currentAttr || parseWeightValue(currentAttr)),
      percentage: finalAttrPercentage
    };
  }
};

/** Helper function to recalculate all percentages
 * Extracted to allow calling from multiple reducers
 */
export const performRecalculateAllPercentages = (state: DatasetState) => {
  const existingDatasets = Object.values(getAvailableDatasets(state.datasets));

  if (!existingDatasets.length) return;

  let totalValidAttributes = 0;

  // Iterate through all datasets to find all valid attributes across datasets
  // Valid attributes are those that have INT or FLOAT data types, or are "Total POI Count" for POI datasets
  existingDatasets.forEach((dataset) => {
    const datasetId = dataset.data.id;

    // Initialize weight values object for the dataset if it doesn't exist
    if (!state.weightValues[datasetId]) {
      state.weightValues[datasetId] = { weight: "", attributes: {} };
    }

    // For dataset of "Polygon", "Admin Area", "H3", "Geohash", "Line" spatial type, filter attributes that have INT or FLOAT data types
    if (
      ["polygon", "adm_area", "H3", "geohash", "line"].includes(
        dataset.properties.spatialType
      )
    ) {
      const validAttributes = getValidNumericAttributes(dataset.categories);

      // Count total valid attributes across datasets
      totalValidAttributes += validAttributes.length;

      // Initialize weight value for each valid attribute in the dataset
      validAttributes.forEach((attribute) => {
        if (!state.weightValues[datasetId].attributes[attribute.id]) {
          state.weightValues[datasetId].attributes[attribute.id] = {
            weight: "0"
          };
        }
      });
    }

    if (dataset.properties.spatialType === "poi") {
      // For dataset with "POI" spatial type, we only have 1 attribute called "Total POI Count"
      totalValidAttributes++;

      // "Total POI Count" is not a defined attribute/schema of a dataset in BE, but BE requires at least one attribute/schema to be defined and passed for the calculation
      // As per BE input, we can use any schemaId from the dataset
      const poiSchemaId = dataset.categories[0].id;

      // Initialize weight value for "Total POI Count" attribute in the dataset
      if (!state.weightValues[datasetId].attributes[poiSchemaId]) {
        state.weightValues[datasetId].attributes[poiSchemaId] = { weight: "0" };
      }
    }
  });

  // Calculate and assign the default weight value for each found attribute
  if (totalValidAttributes > 0) {
    // Equal distribution of weight values across all valid attributes
    // For example, if there are 4 valid attributes (of INT or FLOAT type), each will get a weight of 25 out of 100
    const defaultWeight = (100 / totalValidAttributes).toFixed(2).toString();

    // Assign the calculated default weight value
    Object.entries(state.weightValues).forEach(
      ([datasetId, weightAttributes]) => {
        Object.keys(weightAttributes.attributes).forEach((attributeId) => {
          state.weightValues[datasetId].attributes[attributeId] = {
            weight: defaultWeight
          };
        });
      }
    );
  }
};

/** Calculate the final percentage string for an attribute */
const calculateFinalAttrPercentage = (
  attrWeight: number,
  attrWeightSum: number,
  datasetPercentageStr: string
): string => {
  if (attrWeightSum === 0) return "0";
  const attrPercentageWithinDataset = (attrWeight / attrWeightSum) * 100;
  return (
    (attrPercentageWithinDataset * Number.parseFloat(datasetPercentageStr)) /
    100
  )
    .toFixed(2)
    .replace(/\.00$/, "");
};

/** Set percentage on an attribute weight entry, normalizing to WeightValueEntry if needed */
const updateAttrPercentage = (
  attributes: Record<string, WeightValueEntry>,
  attrId: string,
  attr: WeightPrimitive,
  percentage: string
) => {
  if (typeof attr === "object" && attr !== null) {
    attr.percentage = percentage;
  } else {
    attributes[attrId] = {
      weight: typeof attr === "string" ? attr : String(parseWeightValue(attr)),
      percentage
    };
  }
};

/** Calculate percentages from existing weight values without modifying the weights.
 * Used when loading a configuration where weights are already set by the backend
 * but percentages need to be computed client-side.
 *
 * Unlike performRecalculateAllPercentagesV2, this function:
 * - Does NOT modify existing weight values
 * - Does NOT require state.datasets (operates solely on state.weightValues)
 * - Only adds/updates the `percentage` field on each attribute's WeightValueEntry
 */
export const performCalculatePercentagesFromExistingWeights = (
  state: DatasetState
) => {
  const weightValues = Object.values(state.weightValues);

  // Calculate total dataset weight
  const totalDatasetWeight = weightValues.reduce(
    (sum, entry) => sum + parseWeightValue(entry.weight),
    0
  );

  if (totalDatasetWeight === 0) return;

  for (const entry of weightValues) {
    const datasetWeight = parseWeightValue(entry.weight);
    const datasetPercentageStr = ((datasetWeight / totalDatasetWeight) * 100)
      .toFixed(2)
      .replace(/\.00$/, "");

    // Calculate sum of attribute weights within this dataset
    const attrWeightSum = Object.values(entry.attributes).reduce(
      (sum, attr) => sum + parseWeightValue(attr),
      0
    );

    // Calculate and set percentage for each attribute
    for (const [attrId, attr] of Object.entries(entry.attributes)) {
      const finalAttrPercentage = calculateFinalAttrPercentage(
        parseWeightValue(attr),
        attrWeightSum,
        datasetPercentageStr
      );
      updateAttrPercentage(entry.attributes, attrId, attr, finalAttrPercentage);
    }
  }
};

/** Helper function to recalculate all percentages
 * Extracted to allow calling from multiple reducers
 */
export const performRecalculateAllPercentagesV2 = (state: DatasetState) => {
  const availableDatasets = getAvailableDatasets(state.datasets);
  const totalDatasetWeight = calculateTotalDatasetWeight(state);

  Object.entries(availableDatasets).forEach(([datasetId, dataset]) => {
    // Initialize weight values if needed
    if (!state.weightValues[datasetId]) {
      state.weightValues[datasetId] = { weight: "", attributes: {} };
    }
    if (!state.weightValues[datasetId].weight) {
      state.weightValues[datasetId].weight =
        getDefaultWeightForDataset(dataset).toString();
    }

    const datasetPercentage = calculateWeightPercentage(
      datasetId,
      "weight",
      totalDatasetWeight,
      state.weightValues
    );

    // Sync weight with percentage by default
    const datasetPercentageValueStr = datasetPercentage.replace("%", "");
    state.weightValues[datasetId].weight = datasetPercentageValueStr;

    const validAttributes = getValidAttributesForDataset(dataset);
    const baseWeight = getDefaultWeightForDataset(dataset);

    // Initialize default weights for attributes
    const datasetPercentageValue = Number.parseFloat(datasetPercentageValueStr);
    const calculatedAttributeWeight =
      baseWeight === 0
        ? "100"
        : (datasetPercentageValue / baseWeight).toFixed(2).replace(/\.00$/, "");

    validAttributes.forEach((attr) => {
      if (!state.weightValues[datasetId].attributes[attr.id]) {
        state.weightValues[datasetId].attributes[attr.id] = {
          weight: calculatedAttributeWeight
        };
      } else {
        state.weightValues[datasetId].attributes[attr.id].weight =
          calculatedAttributeWeight;
      }
    });

    const attributeWeightSum = calculateAttributeWeightSum(
      state,
      datasetId,
      validAttributes
    );

    // Update percentages for each attribute
    validAttributes.forEach((attr) => {
      updateAttributePercentage(
        state,
        datasetId,
        attr.id,
        attributeWeightSum,
        datasetPercentage
      );
    });
  });
};
